import React, { useState, useRef, useEffect } from "react";
import io from "socket.io-client";
import { useUploadStore } from "../stores/videoUploadStore";

const socket = io(process.env.REACT_APP_API_URL);

const VideoUpload = ({ surveyId }) => {
  const { videoFile, setVideoFile, uploadProgress, handleUpload, fetchVideoData } =
    useUploadStore();
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState(""); // New state for message
  const [gpxFile, setGpxFile] = useState(null);
  const [frameCount, setFrameCount] = useState("");
  const [detectedObjects, setDetectedObjects] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [videoLabel, setVideoLabel] = useState("");

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const videoInputRef = useRef(null);
  const gpxInputRef = useRef(null);

  // Reset form after processing completes
  useEffect(() => {
    if (processingProgress === 100 && processingMessage === "Processing completed!") {
      resetForm();
      fetchVideoData(surveyId);
    }
  }, [processingProgress, processingMessage, fetchVideoData, surveyId]);

  const resetForm = () => {
    setVideoFile(null);
    setGpxFile(null);
    setVideoLabel("");
    setFrameCount("");
    setProcessingProgress(0);
    setProcessingMessage("");

    // Reset file inputs
    if (videoInputRef.current) videoInputRef.current.value = "";
    if (gpxInputRef.current) gpxInputRef.current.value = "";
  };

  useEffect(() => {
    socket.on("processingUpdate", (update) => {
      console.log("Received processingUpdate:", update);
      if (typeof update.progress === "number") {
        setProcessingProgress(update.progress);
      } else {
        console.warn("Invalid progress value in processingUpdate:", update.progress);
      }
      if (update.message) {
        setFrameCount(update.message);
        setProcessingMessage(update.message);
      } else {
        console.warn("No message in processingUpdate:", update);
      }
    });

    socket.on("objectDetection", (data) => {
      console.log("Received objectDetection:", data);
      if (data.objects && Array.isArray(data.objects)) {
        setDetectedObjects(data.objects);
        drawBoundingBoxes(data.objects);
      } else {
        console.warn("Invalid objectDetection data:", data);
      }
    });

    socket.on("connect", () => {
      console.log("Socket.IO connected");
    });

    socket.on("disconnect", () => {
      console.warn("Socket.IO disconnected");
    });

    socket.on("connect_error", (error) => {
      console.error("Socket.IO connection error:", error);
    });

    return () => {
      socket.off("processingUpdate");
      socket.off("objectDetection");
      socket.off("connect");
      socket.off("disconnect");
      socket.off("connect_error");
    };
  }, []);

  const drawBoundingBoxes = (objects) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const video = videoRef.current;

    if (!canvas || !video) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    objects.forEach(({ label, x, y, width, height }) => {
      ctx.strokeStyle = "red";
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, width, height);
      ctx.fillStyle = "red";
      ctx.font = "16px Arial";
      ctx.fillText(label, x, y - 5);
    });
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("video/")) {
      setVideoFile(file);
    } else {
      alert("Please select a valid video file.");
      e.target.value = "";
    }
  };

  const handleGpxFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".gpx")) {
      setGpxFile(file);
    } else {
      alert("Please select a valid GPX file.");
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!videoFile || !gpxFile || !videoLabel) {
      alert("Please select a video, a GPX file, and enter a video name.");
      return;
    }
    setIsUploading(true);
    try {
      await handleUpload(surveyId, gpxFile, videoLabel);
      // After successful upload and processing, refresh the video list
      await fetchVideoData(surveyId);
    } catch (error) {
      console.error("Error during upload:", error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full mx-auto p-4">
      <div className="flex flex-col items-center justify-between">
        {/* Input Grid */}
        <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Video File Input */}
          <div className="flex flex-col space-y-2">
            <label className="text-gray-700 mb-2 font-semibold">
              Video File
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoFileChange}
              ref={videoInputRef}
              className="w-full text-gray-700 px-5 py-2 rounded-3xl cursor-pointer border border-gray-300"
            />
          </div>

          {/* GPX File Input */}
          <div className="flex flex-col space-y-2">
            <label className="text-gray-700 mb-2 font-semibold">GPX File</label>
            <input
              type="file"
              accept=".gpx"
              onChange={handleGpxFileChange}
              ref={gpxInputRef}
              className="w-full text-gray-700 px-5 py-2 rounded-3xl cursor-pointer border border-gray-300"
            />
          </div>

          {/* Video Name Input - Full width on all screens */}
          <div className="md:col-span-2 flex flex-col space-y-2">
            <label className="text-gray-700 mb-2 font-semibold">
              Video Name
            </label>
            <input
              type="text"
              placeholder="Enter video name"
              value={videoLabel}
              onChange={(e) => setVideoLabel(e.target.value)}
              className="w-full text-gray-700 px-5 py-2 rounded-3xl cursor-pointer border border-gray-300"
            />
          </div>
        </div>

        {/* Upload Button */}
        <button
          onClick={handleSubmit}
          disabled={isUploading}
          className={`px-5 py-2 rounded-3xl mt-6 ${
            isUploading
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-[#5a708b] text-white hover:bg-[#4a5c73]"
          }`}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </button>

        {/* Progress Indicators */}
        <div className="w-full mt-6 space-y-4">
          {uploadProgress > 0 && (
            <div className="w-full">
              <h3 className="text-sm font-semibold mb-2">Uploading Progress</h3>
              <div className="relative w-full h-6 bg-gray-200 rounded-lg">
                <div
                  className="absolute left-0 top-0 h-full bg-blue-600 rounded-lg"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">
                  {uploadProgress}%
                </span>
              </div>
            </div>
          )}

          {processingProgress > 0 && (
            <div className="w-full">
              <h3 className="text-sm font-semibold mb-2">
                Processing Progress
              </h3>
              <div className="relative w-full h-6 bg-gray-200 rounded-lg">
                <div
                  className="absolute left-0 top-0 h-full bg-green-600 rounded-lg"
                  style={{ width: `${processingProgress}%` }}
                ></div>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-white">
                {processingProgress}%{frameCount ? ` - ${frameCount}` : ""}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VideoUpload;
